import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import * as api from '../../../services/api';
import {
  BulkEligibilityAction,
  CriterionComparison,
  CriterionEligibilitySummary,
  EligibilityProgress,
  NotificationState,
} from '../../../types';
import CriteriaIndex from './CriteriaIndex';
import ComparisonPage from './ComparisonPage';
import { pinnedMessage } from './pinAffordance';

/**
 * Two pages behind one route. The criterion lives in `?criteriaId=`, so browser Back
 * and reload behave as pages rather than losing the selection.
 * See docs/specs/policy-version-comparison.md §6.
 */

const PolicyComparison: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const criteriaId = searchParams.get('criteriaId');

  const [summaries, setSummaries] = useState<CriterionEligibilitySummary[] | null>(null);
  const [progress, setProgress] = useState<EligibilityProgress | null>(null);
  const [comparison, setComparison] = useState<CriterionComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadIndex = useCallback(async () => {
    const [rows, totals] = await Promise.all([
      api.fetchCriteriaEligibility(),
      api.fetchEligibilityProgress(),
    ]);
    setSummaries(rows);
    setProgress(totals);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        if (criteriaId) {
          const detail = await api.fetchCriterionComparison(criteriaId);
          if (!cancelled) setComparison(detail);
        } else {
          await loadIndex();
          if (!cancelled) setComparison(null);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [criteriaId, loadIndex]);

  const refreshDetail = useCallback(async () => {
    if (!criteriaId) return;
    // The index totals move whenever a decision lands, so both are refetched -- the
    // "labels recovered" figure is the whole point of the progress display.
    const [detail] = await Promise.all([api.fetchCriterionComparison(criteriaId), loadIndex()]);
    setComparison(detail);
  }, [criteriaId, loadIndex]);

  const handleSetEligibility = useCallback(
    async (policyId: string, eligible: boolean) => {
      setBusy(true);
      try {
        await api.setVersionEligibility(policyId, eligible);
        await refreshDetail();
        setNotification({
          open: true,
          message: eligible ? 'Marked equivalent — labels recovered' : 'Marked different',
          severity: 'success',
        });
      } catch (updateError) {
        setNotification({
          open: true,
          message: updateError instanceof Error ? updateError.message : 'Update failed',
          severity: 'error',
        });
      } finally {
        setBusy(false);
      }
    },
    [refreshDetail]
  );

  const handleBulk = useCallback(
    async (targetId: string, action: BulkEligibilityAction) => {
      setBusy(true);
      try {
        const result = await api.bulkSetEligibility(targetId, action);
        // Refresh whichever page is showing: the detail if one is open, else the index.
        if (criteriaId) await refreshDetail();
        else await loadIndex();
        setNotification({
          open: true,
          message: `${action.replace(/_/g, ' ')}: ${result.versionsChanged} version(s) updated`,
          severity: 'success',
        });
      } catch (bulkError) {
        setNotification({
          open: true,
          message: bulkError instanceof Error ? bulkError.message : 'Bulk update failed',
          severity: 'error',
        });
      } finally {
        setBusy(false);
      }
    },
    [criteriaId, refreshDetail, loadIndex]
  );

  // Pinning shares nothing with eligibility except the refresh: both refetch the
  // criterion so the rail and the index update together. See
  // docs/specs/policy-version-pinning.md.
  const handlePin = useCallback(
    async (policyId: string) => {
      if (!criteriaId) return;
      setBusy(true);
      try {
        const previous = comparison?.pin?.version ?? null;
        const result = await api.pinPolicyVersion(criteriaId, policyId);
        await refreshDetail();
        setNotification({
          open: true,
          message: result.pin ? pinnedMessage(result.pin.version, previous) : 'Pinned',
          severity: 'success',
        });
      } catch (pinError) {
        setNotification({
          open: true,
          message: pinError instanceof Error ? pinError.message : 'Pin failed',
          severity: 'error',
        });
      } finally {
        setBusy(false);
      }
    },
    [criteriaId, comparison, refreshDetail]
  );

  const handleUnpin = useCallback(async () => {
    if (!criteriaId) return;
    setBusy(true);
    try {
      await api.unpinPolicyVersion(criteriaId);
      await refreshDetail();
      setNotification({
        open: true,
        message: 'Unpinned — Stylus follows the current version again',
        severity: 'success',
      });
    } catch (unpinError) {
      setNotification({
        open: true,
        message: unpinError instanceof Error ? unpinError.message : 'Unpin failed',
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  }, [criteriaId, refreshDetail]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {criteriaId && comparison ? (
        <ComparisonPage
          comparison={comparison}
          onBack={() => setSearchParams({})}
          busy={busy}
          onSetEligibility={handleSetEligibility}
          onBulk={action => handleBulk(criteriaId, action)}
          onPin={handlePin}
          onUnpin={handleUnpin}
          onNotify={(message, severity) => setNotification({ open: true, message, severity })}
        />
      ) : (
        <CriteriaIndex
          summaries={summaries ?? []}
          progress={progress}
          onOpen={id => setSearchParams({ criteriaId: id })}
        />
      )}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity}>{notification.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PolicyComparison;
